/**
 * LoadingState Component
 * Standardized loading UI with consistent styling
 */

import { Center, Loader, Stack, Text } from '@mantine/core';
import type { ReactNode } from 'react';

export interface LoadingStateProps {
  /** Loading message */
  message?: string;
  /** Size of loader */
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  /** Variant */
  variant?: 'spinner' | 'dots' | 'bars' | 'oval';
  /** Full height container */
  fullHeight?: boolean;
  /** Additional content */
  children?: ReactNode;
}

export function LoadingState({
  message = 'Loading...',
  size = 'md',
  variant = 'spinner',
  fullHeight = false,
  children,
}: LoadingStateProps) {
  const loaderType = variant === 'spinner' ? 'bars' : variant === 'dots' ? 'dots' : variant === 'bars' ? 'bars' : 'oval';

  const content = (
    <Stack align="center" gap="md">
      <Loader type={loaderType} size={size} aria-label="Loading" />
      {message && (
        <Text size="sm" c="dimmed" aria-live="polite">
          {message}
        </Text>
      )}
      {children}
    </Stack>
  );

  if (fullHeight) {
    return (
      <Center h="100%" py="xl">
        {content}
      </Center>
    );
  }

  return <Center py="xl">{content}</Center>;
}

